import { useState, useCallback, useEffect } from "react";
import "./App.css";
import { GraduationCap, ShieldCheck, BookOpen, UserCircle, ArrowLeft } from "lucide-react";
import { Toaster } from "sonner";
import AdminView from "./components/AdminView";
import TeacherView from "./components/TeacherView";
import StudentView from "./components/StudentView";
import LandingPage from "./components/LandingPage";
import { mockExam } from "./data/mockData";
import api from "./services/api";

const ROLE_LABELS = { admin: "ผู้ดูแลระบบ", teacher: "อาจารย์ผู้สอน", student: "นิสิตผู้เข้าสอบ" };
const ROLE_ICONS = { admin: ShieldCheck, teacher: BookOpen, student: UserCircle };

// Bootstrap exam so students can test immediately (used only if API fails)
const FALLBACK_EXAMS = [{
  id: "exam-sc101-init",
  title: mockExam.title,
  course: "SC101",
  courseName: "ทฤษฎีการสื่อสารมวลชน",
  duration: mockExam.duration,
  status: "เปิดสอบ",
  shuffleQuestions: false,
  shuffleOptions: false,
  questions: mockExam.questions,
  totalScore: mockExam.totalScore,
  createdAt: "01/05/2567, 09:00:00",
  teacherName: "รศ.ดร.สมชาย วงศ์ประภา",
}];

function App() {
  const [appPhase, setAppPhase] = useState("landing");
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [teacherLoggedIn, setTeacherLoggedIn] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [savedExams, setSavedExams] = useState([]);
  const [forceSubmitInfo, setForceSubmitInfo] = useState(null);
  const [overrideGranted, setOverrideGranted] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Load all data from MongoDB on startup ──
  useEffect(() => {
    const loadAll = async () => {
      try {
        const [tRes, cRes, eRes] = await Promise.all([
          api.getTeachers(),
          api.getCourses(),
          api.getExams(),
        ]);
        setTeachers(tRes.data);
        setCourses(cRes.data);
        setSavedExams(eRes.data.length > 0 ? eRes.data : FALLBACK_EXAMS);
      } catch (err) {
        console.error("API load failed, using fallback data:", err);
        setSavedExams(FALLBACK_EXAMS);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // State setters are stable React references — empty deps are correct
  const handleGrantOverride = useCallback(() => { setOverrideGranted(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const handleForceSubmit = useCallback((info) => { setForceSubmitInfo(info); setOverrideGranted(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const handleOverrideUsed = useCallback(() => { setOverrideGranted(false); setForceSubmitInfo(null); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const handleChangePassword = useCallback(async (teacherId, newPassword) => {
    await api.updateTeacherPassword(teacherId, newPassword);
    setTeachers(prev => prev.map(t => t.id === teacherId ? { ...t, password: newPassword } : t));
    setTeacherLoggedIn(prev => prev?.id === teacherId ? { ...prev, password: newPassword } : prev);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const goToLanding = () => setAppPhase("landing");

  if (appPhase === "landing") {
    return (
      <>
        <Toaster richColors position="top-right" />
        <LandingPage onSelectRole={(role) => setAppPhase(role)} />
      </>
    );
  }

  const RoleIcon = ROLE_ICONS[appPhase];

  return (
    <div className="App min-h-screen bg-[#F4F5F6]">
      <Toaster richColors position="top-right" />
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={goToLanding} data-testid="back-to-landing-btn"
              className="flex items-center gap-1.5 text-[#5E636E] hover:text-[#007CB7] text-sm transition-colors mr-1">
              <ArrowLeft size={15} strokeWidth={1.5} />
              <span className="hidden sm:inline">หน้าหลัก</span>
            </button>
            <div className="w-8 h-8 bg-[#007CB7] rounded-lg flex items-center justify-center flex-shrink-0">
              <GraduationCap size={18} color="white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[#1A1B1F] font-semibold text-sm leading-tight" style={{ fontFamily: "'Prompt', sans-serif" }}>
                ระบบสอบออนไลน์ <span className="text-[#007CB7]">BY PICK</span>
              </p>
              <p className="text-[#5E636E] text-xs">วิทยาลัยนวัตกรรมสื่อสารสังคม มศว</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
            {RoleIcon && <RoleIcon size={15} strokeWidth={1.5} className="text-[#007CB7]" />}
            <span className="text-sm font-medium text-[#1A1B1F]" style={{ fontFamily: "'Noto Sans Thai', sans-serif" }}>
              {ROLE_LABELS[appPhase]}
            </span>
          </div>
        </div>
      </header>

      <main>
        {appPhase === "admin" && (
          <AdminView
            loggedIn={adminLoggedIn}
            onLogin={() => setAdminLoggedIn(true)}
            onLogout={() => { setAdminLoggedIn(false); goToLanding(); }}
            teachers={teachers} setTeachers={setTeachers}
            courses={courses} setCourses={setCourses}
            savedExams={savedExams} setSavedExams={setSavedExams}
          />
        )}
        {appPhase === "teacher" && (
          <TeacherView
            teachers={teachers} courses={courses} setCourses={setCourses}
            teacherLoggedIn={teacherLoggedIn}
            onLogin={(teacher) => setTeacherLoggedIn(teacher)}
            onLogout={() => { setTeacherLoggedIn(null); goToLanding(); }}
            savedExams={savedExams} setSavedExams={setSavedExams}
            forceSubmitInfo={forceSubmitInfo}
            onGrantOverride={handleGrantOverride}
            onChangePassword={handleChangePassword}
          />
        )}
        {appPhase === "student" && (
          <StudentView
            savedExams={savedExams}
            overrideGranted={overrideGranted}
            onForceSubmit={handleForceSubmit}
            onOverrideUsed={handleOverrideUsed}
          />
        )}
      </main>
    </div>
  );
}

export default App;

